import React, { useMemo, useState } from 'react'
import { localizedText } from '../utils/localized'
import { useNavigate, useParams } from 'react-router-dom'
import { FaEye, FaFileAlt, FaFilePdf, FaFolderOpen, FaPaperclip } from 'react-icons/fa'
import { useCurrent, useFetch } from '../hooks'
import { CardHeaderWithActions, MwButton, MwSpinnerButton, NoDataFound } from '../components/ui'
import {
    getCatalogLabel,
    getPriorityAppearance,
    getPriorityLabel,
    getStatusAppearance,
    getStatusLabel,
    normalizeSupportSettings
} from '../constants/ticket'
import { toSafeHtml } from '../utils/richTextHtml'
import { getAttachmentInfo, normalizeAttachmentList } from '../utils/attachmentNames'
import { ProjectStatusPill, formatDate, getTimeProgress } from '../components/projects/projectView'

const Field = ({ label, children }) => (
    <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflowWrap: 'anywhere' }}>{children || '-'}</div>
    </div>
);

const Pill = ({ appearance, children }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 999, background: appearance?.bg || '#f1f5f9', color: appearance?.color || '#475569', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: appearance?.dot || appearance?.color || '#94a3b8' }} />
        {children}
    </span>
);

// صفحة المشروع للعميل: بيانات المشروع وتذاكره الخارجية — عرض فقط من غير أي إجراء.
const ProjectShow = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { currentLangId } = useCurrent();
    const isAr = currentLangId !== 'en';
    const [statusFilter, setStatusFilter] = useState('all');
    const [sprintFilter, setSprintFilter] = useState('all');
    const { data: projectResult, loading, error } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/projects/customer/${params.id}`);
    const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const { data: statusesResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses`);
    const supportSettings = useMemo(() => ({
        ...normalizeSupportSettings(supportSettingsResult),
        statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
    }), [statusesResult, supportSettingsResult]);

    const project = projectResult?.data || null;
    const tickets = useMemo(() => (Array.isArray(project?.tickets) ? project.tickets : []), [project]);
    const sprints = Array.isArray(project?.sprints) ? project.sprints : [];
    const statusCounts = useMemo(() => {
        const counts = {};
        tickets.forEach((ticket) => {
            const key = String(ticket.statusId || ticket.status || '');
            counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
    }, [tickets]);
    const visibleTickets = tickets.filter((ticket) =>
        (statusFilter === 'all' || String(ticket.statusId || ticket.status || '') === statusFilter) &&
        (sprintFilter === 'all' || String(ticket.sprintId || '') === sprintFilter)
    );
    const attachments = normalizeAttachmentList(project?.attachments);
    const progress = getTimeProgress(project);

    if (loading && !project) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><MwSpinnerButton withLabel={false} isFullCenter /></div>;
    }

    if (!project) {
        return (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24, textAlign: 'center', color: '#b91c1c', fontWeight: 700, fontSize: 13 }}>
                {error?.response?.status === 404 || projectResult
                    ? (isAr ? 'المشروع غير موجود' : 'Project not found')
                    : (isAr ? 'تعذر تحميل المشروع' : 'Failed to load project')}
            </div>
        );
    }

    const section = project.sectionId ? getCatalogLabel(supportSettings.sections, project.sectionId, currentLangId) : '';
    const product = project.ProductId ? getCatalogLabel(supportSettings.products, project.ProductId, currentLangId) : '';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <CardHeaderWithActions title={isAr ? 'تفاصيل المشروع' : 'Project details'}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 800 }}>
                    <FaEye size={11} />
                    {isAr ? 'عرض فقط' : 'View only'}
                </span>
                <MwButton type='secondary' onClick={() => navigate(`/${params.tenant}/projects`)}>
                    {isAr ? 'المشاريع' : 'Projects'}
                </MwButton>
            </CardHeaderWithActions>

            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #c7d2fe', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: '#eef2ff', borderBottom: '1px solid #c7d2fe' }}>
                    <span style={{ width: 34, height: 34, borderRadius: 10, background: '#4f46e5', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FaFolderOpen size={16} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, fontWeight: 800, color: '#4f46e5' }}>
                            <span>{isAr ? 'المشروع' : 'Project'} #{project.projectNumber ?? '-'}</span>
                            <ProjectStatusPill status={project.status} lang={currentLangId} />
                        </div>
                        <div dir="auto" style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', overflowWrap: 'anywhere' }}>{localizedText(project, 'title', currentLangId)}</div>
                    </div>
                </div>
                <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 14 }}>
                        <Field label={isAr ? 'تاريخ البداية' : 'Start date'}>{formatDate(project.startAt)}</Field>
                        <Field label={isAr ? 'النهاية المتوقعة' : 'Expected end'}>{formatDate(project.expectedAt)}</Field>
                        <Field label={isAr ? 'القسم / الخدمة' : 'Section / Product'}>{[section, product].filter(Boolean).join(' / ')}</Field>
                        <Field label={isAr ? 'رقم العقد' : 'Contract #'}>{project.contractNumber}</Field>
                        <Field label={isAr ? 'مسؤول المشروع' : 'Project managers'}>{(project.managers || []).join('، ')}</Field>
                        <Field label={isAr ? 'عدد التذاكر' : 'Tickets'}>{tickets.length}</Field>
                    </div>
                    {progress !== null && (
                        <div>
                            <div style={{ height: 8, borderRadius: 999, background: '#f1f5f9', overflow: 'hidden' }}>
                                <div style={{ width: `${progress}%`, height: '100%', background: progress >= 100 ? '#f59e0b' : '#6366f1' }} />
                            </div>
                            <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{isAr ? `مر ${progress}% من مدة المشروع` : `${progress}% of the project time elapsed`}</div>
                        </div>
                    )}
                    {localizedText(project, 'description', currentLangId) && (
                        <div style={{ paddingTop: 14, borderTop: '1px dashed #e2e8f0' }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 8 }}>{isAr ? 'وصف المشروع' : 'Description'}</div>
                            <div className="mw-rich-content" dir="auto" style={{ fontSize: 13, lineHeight: 1.85, color: '#334155', overflowWrap: 'anywhere' }} dangerouslySetInnerHTML={{ __html: toSafeHtml(localizedText(project, 'description', currentLangId)) }} />
                        </div>
                    )}
                    {attachments.length > 0 && (
                        <div style={{ paddingTop: 14, borderTop: '1px dashed #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 10 }}>
                                <FaPaperclip size={10} /> {isAr ? 'مرفقات المشروع' : 'Project attachments'}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                                {attachments.map((url) => {
                                    const info = getAttachmentInfo(url, project.attachmentNames);
                                    return (
                                        <a key={url} href={url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 12, background: '#f8fafc', border: '1.5px solid #e2e8f0', textDecoration: 'none', minWidth: 0 }}>
                                            <span style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0, background: info.isPdf ? '#fff1f2' : '#f0f9ff', color: info.isPdf ? '#ef4444' : '#0ea5e9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {info.isPdf ? <FaFilePdf size={16} /> : <FaFileAlt size={16} />}
                                            </span>
                                            <span style={{ minWidth: 0 }}>
                                                <span dir="auto" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.name}</span>
                                                <span style={{ display: 'block', fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{info.ext}</span>
                                            </span>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{isAr ? 'تذاكر المشروع' : 'Project tickets'}</span>
                    <span style={{ minWidth: 26, padding: '2px 8px', borderRadius: 999, background: '#eef2ff', color: '#4f46e5', fontSize: 11, fontWeight: 900, textAlign: 'center' }}>{visibleTickets.length}</span>
                    {sprints.length > 0 && (
                        <select
                            value={sprintFilter}
                            onChange={(event) => setSprintFilter(event.target.value)}
                            style={{ marginInlineStart: 'auto', height: 34, borderRadius: 10, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 12, fontWeight: 700 }}
                        >
                            <option value="all">{isAr ? 'كل الدورات' : 'All sprints'}</option>
                            {sprints.map((sprint) => (
                                <option key={sprint._id} value={sprint._id}>{isAr ? 'دورة' : 'Sprint'} {sprint.sprintNumber}{localizedText(sprint, 'title', currentLangId) ? ` — ${localizedText(sprint, 'title', currentLangId)}` : ''}</option>
                            ))}
                        </select>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                    <button type="button" onClick={() => setStatusFilter('all')} style={{ padding: '5px 12px', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 800, background: statusFilter === 'all' ? '#0f172a' : '#f1f5f9', color: statusFilter === 'all' ? '#fff' : '#475569' }}>
                        {isAr ? 'الكل' : 'All'} ({tickets.length})
                    </button>
                    {Object.keys(statusCounts).map((statusId) => {
                        const active = statusFilter === statusId;
                        const appearance = getStatusAppearance(supportSettings.statuses, statusId);
                        return (
                            <button key={statusId} type="button" onClick={() => setStatusFilter(statusId)} style={{ padding: '5px 12px', borderRadius: 999, border: `1px solid ${active ? appearance?.color || '#0f172a' : 'transparent'}`, cursor: 'pointer', fontSize: 11, fontWeight: 800, background: appearance?.bg || '#f1f5f9', color: appearance?.color || '#475569' }}>
                                {getStatusLabel(supportSettings.statuses, statusId, currentLangId) || '-'} ({statusCounts[statusId]})
                            </button>
                        );
                    })}
                </div>

                {!visibleTickets.length ? (
                    <NoDataFound msg={isAr ? 'لا توجد تذاكر' : 'No tickets'} />
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: 720 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 140px 120px 110px 110px', gap: 8, padding: '10px 14px', background: '#f8fafc', borderRadius: 10, fontSize: 10, fontWeight: 800, color: '#94a3b8' }}>
                                <div style={{ textAlign: 'center' }}>{isAr ? 'رقم التذكرة' : 'Ticket #'}</div>
                                <div>{isAr ? 'العنوان' : 'Subject'}</div>
                                <div style={{ textAlign: 'center' }}>{isAr ? 'الحالة' : 'Status'}</div>
                                <div style={{ textAlign: 'center' }}>{isAr ? 'الأولوية' : 'Priority'}</div>
                                <div style={{ textAlign: 'center' }}>{isAr ? 'تاريخ الإنشاء' : 'Created'}</div>
                                <div style={{ textAlign: 'center' }}>{isAr ? 'الوقت المتوقع' : 'Expected'}</div>
                            </div>
                            {visibleTickets.map((ticket) => {
                                const statusId = ticket.statusId || ticket.status;
                                return (
                                    <div
                                        key={ticket._id}
                                        onClick={() => navigate(`/${params.tenant}/tickets/show/${ticket._id}?project=${project._id}`)}
                                        style={{ display: 'grid', gridTemplateColumns: '90px 1fr 140px 120px 110px 110px', gap: 8, alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 12, color: '#475569', cursor: 'pointer' }}
                                        onMouseEnter={(event) => { event.currentTarget.style.background = '#f8fafc'; }}
                                        onMouseLeave={(event) => { event.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <div style={{ textAlign: 'center', fontWeight: 800, color: '#0f172a' }}>{ticket.ticketNumber}</div>
                                        <div style={{ minWidth: 0 }}>
                                            <div dir="auto" title={localizedText(ticket, 'subject', currentLangId)} style={{ fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{localizedText(ticket, 'subject', currentLangId)}</div>
                                            {ticket.sprintNumber && <span style={{ fontSize: 10, fontWeight: 800, color: '#0e7490' }}>{isAr ? 'دورة' : 'Sprint'} {ticket.sprintNumber}</span>}
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <Pill appearance={getStatusAppearance(supportSettings.statuses, statusId)}>{getStatusLabel(supportSettings.statuses, statusId, currentLangId) || '-'}</Pill>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <Pill appearance={getPriorityAppearance(ticket.priority || 'medium')}>{getPriorityLabel(ticket.priority || 'medium', currentLangId)}</Pill>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>{formatDate(ticket.createdAt)}</div>
                                        <div style={{ textAlign: 'center' }}>{formatDate(ticket.expectedAt)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectShow;
